const Logger = require("../../Logger");

const UNEXPECTED_ERROR = "E999999999";

const BASE_VOLUME_COMMAND_BYTES = [ 0x6B, 0x66, 0x20, 0x30, 0x31, 0x20, 0x46, 0x46, 0x0D ];
const READ_VOLUME_COMMAND = new Uint8Array( BASE_VOLUME_COMMAND_BYTES );

const POWER_STATUS_CHECK_COMMAND = new Uint8Array([ 0x6B, 0x61, 0x20, 0x30, 0x31, 0x20, 0x46, 0x46, 0x0D ]);

const POWER_STATUS_CHECK_ACKNOWLEDGEMENT_START_BYTE = 0x61;
const VOLUME_ACKNOWLEDGEMENT_START_BYTE = 0x66;
const VALID_ACKNOWLEDGEMENT_START_BYTES = [
  POWER_STATUS_CHECK_ACKNOWLEDGEMENT_START_BYTE, VOLUME_ACKNOWLEDGEMENT_START_BYTE
];

const POWER_ON_BYTE = 0x31;
const POWER_OFF_BYTE = 0x30;

const LG_WEB_SERIAL_API_OPTIONS = {
  baudRate: 9600,
  dataBits: 8,
  stopBits: 1,
  parity: "none",
  flowControl: "none"
};

const NEW = 1;
const NEXT_MATCH = 2;
const FULL_MATCH = 3;
const NO_NEW_MATCH = 4;
const NO_NEXT_MATCH = 5;
const ERROR = 10;

const service = {
  volumeListeners: [],
  powerStatusListeners: [],
};

service.genericBufferManager = {
  buffer: [],
  offer: b => {
    service.genericBufferManager.buffer.push( b );
  },
  offerAll: bytes => {
    service.genericBufferManager.buffer.push( ...bytes );
  },
  flush: () => {
    const buffer = service.genericBufferManager.buffer;

    if( buffer.length === 0 ) {
      return;
    }

    console.log( "Generic unhandled message", buffer.toString() );
    service.genericBufferManager.clean();
  },
  clean: () => {
    service.genericBufferManager.buffer = [];
  },
};

service.acknowledgementBufferManager = {
  buffer: [],
  getAcknowledgementType: () => service.acknowledgementBufferManager.buffer.length ?
    service.acknowledgementBufferManager.buffer[0] : null,
  offer: b => {
    const buffer = service.acknowledgementBufferManager.buffer;

    switch( buffer.length ) {
      case 0:
        if( VALID_ACKNOWLEDGEMENT_START_BYTES.includes(b) ) {
          buffer.push( b );
          return NEW;
        }

        return NO_NEW_MATCH;
      case 1: case 4:
        if( b === 0x20 ) {
          buffer.push( b );
          return NEXT_MATCH;
        }

        return NO_NEXT_MATCH;
      case 2: case 3: case 5: case 6: case 7: case 8:
        buffer.push( b );
        return NEXT_MATCH;

      case 9:
        if( b === 0x78 ) {
          buffer.push( b );
          return FULL_MATCH;
        }

        return NO_NEXT_MATCH;
      default:
        return ERROR;
    }
  },
  clean: () => {
    service.acknowledgementBufferManager.buffer = [];
  },
};

service.getType = () => "LG";

service.areWebSerialApiOptionsValid = ( webSerialApiOptions ) => {
  return webSerialApiOptions.baudRate === 9600 &&
    webSerialApiOptions.dataBits === 8 &&
    webSerialApiOptions.stopBits === 1 &&
    webSerialApiOptions.parity === "none" &&
    webSerialApiOptions.flowControl === "none";
}

service.arePowerCommandsValid = ( powerOnCommand, powerOffCommand ) => {
  return powerOnCommand.length === 9 && // power on and power off commands are 9 bytes long
    powerOffCommand.length === 9 &&
    powerOnCommand[8] === 0x0D && // termination strings for any LG command
    powerOffCommand[8] === 0x0D;
}

service.getReadVolumeCommand = () => READ_VOLUME_COMMAND;
service.getPowerStatusCheckCommand = () => POWER_STATUS_CHECK_COMMAND;

service.getVolumeCommandFor = volumePercentage => {
  if( volumePercentage < 0 || volumePercentage > 100 ) {
    throw new Error( `Volume percentage ${ volumePercentage } is out of range` );
  }

  const hex = volumePercentage.toString( 16 ).padStart( 2, '0' );
  const high = hex.charCodeAt( 0 );
  const low = hex.charCodeAt( 1 );

  const volumeCommandBytes = [ ...BASE_VOLUME_COMMAND_BYTES ];
  volumeCommandBytes[6] = high;
  volumeCommandBytes[7] = low;

  return new Uint8Array( volumeCommandBytes );
}

service.getDeviceSpecificWebSerialApiOptions = () => ({ ...LG_WEB_SERIAL_API_OPTIONS });

const isInRange = value => ( value >= 0x30 && value <= 0x39 ) || ( value >= 0x61 && value <= 0x66 );

const checkSuccessBytes = ( buffer, messageType ) => {
  if( buffer[5] !== 0x4F || buffer[6] !== 0x4B ) {
    throw new Error( `${ messageType } could not be retrieved from device: '${ buffer.toString() }'` );
  }
}

const extractVolume = buffer => {
  checkSuccessBytes( buffer, 'Volume' );

  const upper = buffer[7];
  const lower = buffer[8];

  if( !isInRange(upper) || !isInRange(lower) ) {
    throw new Error( `Invalid retrieved volume format: '${ buffer.toString() }'` );
  }

  const hex = `${ String.fromCharCode(upper) }${ String.fromCharCode(lower) }`;
  const volume = parseInt( hex, 16 );

  if( volume < 0 || volume > 100 ) {
    throw new Error( `Retrieved volume value ${ volume } is out of range: '${ buffer.toString() }'` );
  }

  return volume;
};

const processVolumeAcknowledgementMessage = buffer => {
  try {
    const volume = extractVolume( buffer );

    console.warn( "TV volume retrieved", volume );

    service.volumeListeners.forEach( listener => listener.onVolume( volume ) );
  } catch( error ) {
    console.warn( "Error extracting volume", error );
    service.volumeListeners.forEach( listener => listener.onError( error ) );
  }
}

const extractPowerStatus = buffer => {
  checkSuccessBytes( buffer, 'Power status' );

  const powerStatusByte = buffer[8];

  if( powerStatusByte !== POWER_ON_BYTE && powerStatusByte !== POWER_OFF_BYTE ) {
    throw new Error( `Invalid retrieved power status format: '${ buffer.toString() }'` );
  }

  return powerStatusByte === POWER_ON_BYTE;
}

const processStatusCheckAcknowledgementMessage = buffer => {
  try {
    const powerStatus = extractPowerStatus( buffer );

    service.powerStatusListeners.forEach( listener => listener.onPowerStatus( powerStatus ) );
  } catch( error ) {
    console.warn( "Error extracting power status", error );
    service.powerStatusListeners.forEach( listener => listener.onError( error ) );
  }
}

const processAcknowledgementMessage = () => {
  const acknowledgementType = service.acknowledgementBufferManager.getAcknowledgementType();
  const buffer = service.acknowledgementBufferManager.buffer;

  switch( acknowledgementType ) {
    case POWER_STATUS_CHECK_ACKNOWLEDGEMENT_START_BYTE:
      processStatusCheckAcknowledgementMessage( buffer );
      break;
    case VOLUME_ACKNOWLEDGEMENT_START_BYTE:
      processVolumeAcknowledgementMessage( buffer );
      break;
    default:
      Logger.viewerError( UNEXPECTED_ERROR, "Invalid acknowledgement type", {acknowledgementType} );
      break;
  }
}

service.handleSerialPortData = bytes => {
  console.log( "LGSerialPortCommunicationHandler.handleSerialPortData", bytes );

  for( const b of bytes ) {
    const volumeBufferOutcome = service.acknowledgementBufferManager.offer( b );

    switch( volumeBufferOutcome ) {
      case NEW: // hold
        service.genericBufferManager.flush(); // flush any prrevious bytes
        break;
      case NEXT_MATCH: // hold
        break;
      case FULL_MATCH:
        processAcknowledgementMessage();
        service.acknowledgementBufferManager.clean();
        break;
      case NO_NEXT_MATCH:
        service.genericBufferManager.offerAll( service.acknowledgementBufferManager.buffer );
        /* falls through: continue handling current byte as NO_NEW_MATCH */
      case NO_NEW_MATCH:
        service.genericBufferManager.offer( b );
        service.acknowledgementBufferManager.clean();
        break;
      default:
        Logger.viewerError( UNEXPECTED_ERROR, "Invalid state", {input: bytes.toString()} );

        break;
    }
  }

  service.genericBufferManager.flush();
}

service.addVolumeListener = listener => {
  if( !listener || typeof listener.onVolume !== 'function' || typeof listener.onError !== 'function' ) {
    throw new Error( 'Listener must be an object with onVolume and onError methods' );
  }

  service.volumeListeners.push(listener);
};

service.removeVolumeListener = listener => {
  service.volumeListeners = service.volumeListeners.filter(l => l !== listener);
};

service.addPowerStatusListener = listener => {
  if( !listener || typeof listener.onPowerStatus !== 'function' || typeof listener.onError !== 'function' ) {
    throw new Error( 'Listener must be an object with onPowerStatus and onError methods' );
  }

  service.powerStatusListeners.push(listener);
};

service.removePowerStatusListener = listener => {
  service.powerStatusListeners = service.powerStatusListeners.filter(l => l !== listener);
};

service.clean = () => {
  service.volumeListeners = [];
  service.powerStatusListeners = [];
  service.genericBufferManager.clean();
  service.acknowledgementBufferManager.clean();
};

module.exports = service;
